import {
  all,
  put,
  takeEvery,
  call,
  select
} from 'redux-saga/effects'

import * as Api from 'api/contacts_management'
import * as AppActions from 'actions/app'
import * as Actions from 'actions/contact_management'
import * as Selectors from 'selectors'

import { downloadBlob } from 'utils/sagas'

import { ExcelMimeType } from 'static/constants'

export function* uploadFile({ payload: file }) {
  const i18n = yield select(Selectors.getI18n)
  const formData = [
    {
      key: 'file',
      value: file
    }
  ]

  let result
  try {
    result = yield call(Api.importUploadFile, formData)

    yield put(Actions.importUploadFileSuccess(result))
  } catch (error) {
    yield put(Actions.importUploadFileError(error))

    let msg = i18n.get('failed')

    if (error.response && error.response.statusCode === 400) {
      msg = error.response.message
    }

    yield put(
      AppActions.showAppSnackbarMessage({
        text: msg,
        variant: 'error'
      })
    )
  }
}

export function* setupImport() {
  const i18n = yield select(Selectors.getI18n)
  const uploadResult = yield select(Selectors.getCmImUploadResult)
  const selectedSheet = yield select(Selectors.getCmImSelectedSheet)
  const useAiColumnMapping = yield select(Selectors.getCmImUseAiColumnMapping)

  const body = {
    file_id: uploadResult.get('file_id'),
    type: uploadResult.get('type'),
    delimiter: uploadResult.get('delimiter') || null,
    selected_sheet: selectedSheet || null,
    use_ai_column_mapping: useAiColumnMapping === 'yes'
  }

  try {
    const result = yield call(Api.importSetup, body)

    yield put(Actions.importSetupSuccess(result))
  } catch (error) {
    yield put(Actions.importSetupError(error))

    let msg = i18n.get('failed')

    if (error.response && error.response.statusCode === 400) {
      msg = error.response.message
    }

    yield put(
      AppActions.showAppSnackbarMessage({
        text: msg,
        variant: 'error'
      })
    )
  }
}

export function* finalizeImport() {
  const i18n = yield select(Selectors.getI18n)
  const uploadResult = yield select(Selectors.getCmImUploadResult)
  const selectedSheet = yield select(Selectors.getCmImSelectedSheet)
  const firstRowHasHeaders = yield select(Selectors.getCmImFirstRowHasHeaders)
  const columnMapping = yield select(Selectors.getCmImColumnMapping)
  const autoAddToLists = yield select(Selectors.getCmImAutoAddToLists)
  const autoCreateLists = yield select(Selectors.getCmImAutoCreateLists)
  const selectedListIds = yield select(Selectors.getCmImSelectedListIds)
  const newListNames = yield select(Selectors.getCmImNewListNames)

  const body = {
    file_id: uploadResult.get('file_id'),
    type: uploadResult.get('type'),
    delimiter: uploadResult.get('delimiter') || null,
    selected_sheet: selectedSheet || null,
    first_row_has_headers: firstRowHasHeaders === 'yes',
    column_mapping: columnMapping,
    selected_list_ids: autoAddToLists ? selectedListIds : [],
    new_list_names: autoCreateLists ? newListNames : []
  }

  try {
    const result = yield call(Api.importFinalize, body)

    if (autoAddToLists || autoCreateLists) {
      yield put(Actions.fetchDistributionListsStart())
    }

    yield put(Actions.clearContacts())
    yield put(Actions.fetchContactsStart())
    yield put(Actions.importFinalizeSuccess(result))
  } catch (error) {
    yield put(Actions.importFinalizeError(error))

    let msg = i18n.get('failed')

    if (error.response && error.response.statusCode === 400) {
      msg = error.response.message
    }

    yield put(
      AppActions.showAppSnackbarMessage({
        text: msg,
        variant: 'error'
      })
    )
  }
}

export function* downloadFailedContacts({ payload: { contacts, sheetName } }) {
  const contactData = contacts.map(contact => ({
    first_name: contact.get('firstName'),
    last_name: contact.get('lastName'),
    mail: contact.get('mail'),
    title: contact.get('title'),
    job_title: contact.get('jobTitle'),
    dossier: contact.get('dossier'),
    picture: contact.get('picture'),
    address: contact.get('address'),
    phone: contact.get('phone'),
    fax: contact.get('fax'),
    external_publication: contact.get('externalPublication'),
    website: contact.get('website'),
    company: contact.get('company'),
    country: contact.get('country'),
    state: contact.get('state'),
    linkedin_url: contact.get('linkedinUrl'),
    twitter_url: contact.get('twitterUrl'),
    facebook_url: contact.get('facebookUrl'),
    threads_url: contact.get('threadsUrl'),
    tiktok_url: contact.get('tiktokUrl'),
    instagram_url: contact.get('instagramUrl'),
    youtube_url: contact.get('youtubeUrl'),
    other_url: contact.get('otherUrl'),
    languages: contact.get('languages'),
    error: contact.get('error')
  }))
  try {
    const body = {
      contacts: contactData,
      sheet_name: sheetName
    }
    const result = yield call(Api.downloadFailed, body)
    yield call(downloadBlob, result, 'contacts.xlsx', ExcelMimeType)
    yield put(Actions.downloadImportFailedContactsSuccess(result))
  } catch (error) {
    yield put(Actions.downloadImportFailedContactsError(error))
    yield put(
      AppActions.showAppMessage({
        success: false
      })
    )
  }
}

export function* cleanupImportFile() {
  const uploadResult = yield select(Selectors.getCmImUploadResult)
  const fileId = uploadResult.get('file_id')

  try {
    if (fileId) {
      const body = {
        file_id: fileId
      }

      yield call(Api.cleanupImportFile, body)
    }
  } finally {
    yield put(Actions.resetImportState())
  }
}

export function* replaceContacts() {
  const i18n = yield select(Selectors.getI18n)
  const contactsToReplace = yield select(Selectors.getCmImSelectedContactsToReplace)
  const importResult = yield select(Selectors.getCmImResult)
  const contactIdsToRemove = contactsToReplace.map(el => el.get('id'))
  const journalistIdsToImport = contactsToReplace.map(el => el.get('suggestionId'))
  const distributionListIds = importResult.get('distributionListIds')

  try {
    const body = {
      contact_ids_to_remove: contactIdsToRemove.toJS(),
      journalist_ids_to_import: journalistIdsToImport.toJS(),
      distribution_list_ids: distributionListIds.toJS()
    }

    yield call(Api.replaceContacts, body)
    yield put(Actions.clearContacts())
    yield put(Actions.fetchContactsStart())
    yield put(Actions.fetchDistributionListsStart())
    yield put(Actions.replaceContactsSuccess(contactIdsToRemove))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('success'),
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.replaceContactsError())
    yield put(
      AppActions.showAppMessage({
        success: false
      })
    )
  }
}

export function* watchImportUploadFileStart() {
  yield takeEvery(Actions.importUploadFileStart, uploadFile)
}

export function* watchImportSetupStart() {
  yield takeEvery(Actions.importSetupStart, setupImport)
}

export function* watchImportFinalizeStart() {
  yield takeEvery(Actions.importFinalizeStart, finalizeImport)
}

export function* watchDownloadImportFailedContactsStart() {
  yield takeEvery(Actions.downloadImportFailedContactsStart, downloadFailedContacts)
}

export function* watchCloseDetailPages() {
  yield takeEvery(Actions.closeDetailPages, cleanupImportFile)
}

export function* watchReplaceContactsStart() {
  yield takeEvery(Actions.replaceContactsStart, replaceContacts)
}

export default function* importSaga() {
  yield all([
    watchImportUploadFileStart(),
    watchImportSetupStart(),
    watchImportFinalizeStart(),
    watchDownloadImportFailedContactsStart(),
    watchCloseDetailPages(),
    watchReplaceContactsStart()
  ])
}
